import { Submittal } from '.'
import { NotificationEventItem } from '../NotificationEventItem'
import { ComplexEnumItem } from '../../../utils/complexEnum/ComplexEnumItem'
import { listStringify } from '../../../utils/listStringify'
import {
  resolveValueOrArray,
  ValueOrArray,
} from '../../../utils/resolveValueOrArray'

/**
 * generates a common NotificationEventItem for reverted notification events
 *
 * the key for this NotificationEventItem should be `Reverted`
 *
 * ---
 *
 * the name is: Reverted
 *
 * in the description, `from` and `to` use {@link listStringify} with `or` and
 * `noComma` enabled
 *
 * the description is: A `{revertable}` status is reverted from `{...from}`, to `{...to}`
 *
 * @param options {@link RevertedOptions}
 * @see {@link NotificationEventItem}
 */
export function RevertedNotificationEventItem<ID extends Lowercase<string>>({
  id,
  revertable,
  ...rest
}: RevertedOptions<ID>) {
  const options = { or: true, noComma: true }
  const from = listStringify({
    items: resolveValueOrArray(rest.from).map(resolve),
    ...options,
  })
  const to = listStringify({
    items: resolveValueOrArray(rest.to).map(resolve),
    ...options,
  })
  return new NotificationEventItem({
    id,
    name: 'Reverted',
    description: `A ${revertable} status is reverted from ${from}, to ${to}`,
  })
}

function resolve(item: string | Pick<NotificationEventItem, 'name'>) {
  return typeof item === 'string' ? item : item.name
}

type RevertedOptions<T extends Lowercase<string>> = Pick<
  ComplexEnumItem<T>,
  'id'
> & {
  /**
   * which revertable object this is for
   */
  revertable:
    | Submittal
    | 'Field Design Change'
    | 'Request For Information'
    | 'Identified Field Issue'
    | 'Issue'
  /**
   * which status(es) we're reverting from
   */
  from: ValueOrArray<string | Pick<NotificationEventItem, 'name'>>
  /**
   * which status(es) we're reverting to
   */
  to: ValueOrArray<string | Pick<NotificationEventItem, 'name'>>
}
